import React from 'react'
import {
  Box,
  Button,
  Typography,
  Chip,
  Alert,
  Grid,
} from '@mui/material'
import { useTranslation, Trans } from 'react-i18next' // LAP-733 (GE022)
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import * as XLSX from 'xlsx'

function FileUploadStep({
  uploadedFile,
  setUploadedFile,
  fileData,
  setFileData,
  setError,
  setLoading,
  onNext,
  onBack,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    const validExtensions = ['.csv', '.xlsx', '.xls']
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (!validExtensions.includes(fileExtension)) {
      setError(t('mesComptes.import.errors.invalidFileType')) // LAP-733 (GE022)
      return
    }

    setUploadedFile(file)
    parseCSVColumns(file)
    setError(null)
  }

  const parseCSVColumns = (file) => {
    const fileName = file.name.toLowerCase()

    if (fileName.endsWith('.csv')) {
      parseCSVFile(file)
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      parseExcelFile(file)
    }
  }

  // Decode raw file bytes keeping accents intact: try UTF-8 strictly, then fall
  // back to Latin-1 (Windows-1252 superset) used by most European/Swiss banks.
  const decodeBuffer = (buffer) => {
    const bytes = new Uint8Array(buffer)
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    } catch (e) {
      return new TextDecoder('iso-8859-1').decode(bytes)
    }
  }

  // Detect the delimiter (tab / semicolon / comma) from the header line by
  // picking the candidate that appears most often. Tabs and semicolons are
  // common in European/Swiss bank exports; defaults to comma if none found.
  const detectDelimiter = (line) => {
    const counts = {
      '\t': (line.match(/\t/g) || []).length,
      ';': (line.match(/;/g) || []).length,
      ',': (line.match(/,/g) || []).length,
    }
    const best = Object.keys(counts).reduce((a, b) => (counts[b] > counts[a] ? b : a))
    return counts[best] > 0 ? best : ','
  }

  const parseCSVFile = (file) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = decodeBuffer(event.target.result)
        const lines = text.split(/\r?\n/).filter(line => line.trim() !== '')

        if (lines.length > 0) {
          // Extract header row (first line), stripping a possible BOM
          const firstLine = lines[0].replace(/^\ufeff/, '')
          const delimiter = detectDelimiter(firstLine)
          const headers = firstLine.split(delimiter).map(col => col.trim().replace(/^"|"$/g, ''))

          setFileData({
            columns: headers,
            rowCount: lines.length - 1,
            delimiter: delimiter
          })
        }
      } catch (error) {
        setError(t('mesComptes.import.errors.parseCsv')) // LAP-733 (GE022)
        console.error('CSV parsing error:', error)
      }
    }
    reader.onerror = () => {
      setError(t('mesComptes.import.errors.readCsv')) // LAP-733 (GE022)
    }
    reader.readAsArrayBuffer(file)
  }

  const parseExcelFile = (file) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Get the first sheet
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]

        // Convert to JSON to get headers and data
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

        if (jsonData.length > 0) {
          // First row is headers
          const headers = jsonData[0]
          const rowCount = jsonData.length - 1

          setFileData({
            columns: headers,
            rowCount: rowCount,
            delimiter: ','
          })
        } else {
          setError(t('mesComptes.import.errors.excelEmpty')) // LAP-733 (GE022)
        }
      } catch (error) {
        setError(t('mesComptes.import.errors.parseExcel')) // LAP-733 (GE022)
        console.error('Excel parsing error:', error)
      }
    }
    reader.onerror = () => {
      setError(t('mesComptes.import.errors.readExcel')) // LAP-733 (GE022)
    }
    reader.readAsArrayBuffer(file)
  }

  const handleValidateAndNext = () => {
    if (!uploadedFile) {
      setError(t('mesComptes.import.errors.uploadRequired')) // LAP-733 (GE022)
      return
    }

    setError(null)
    onNext()
  }

  const Input = ({ children, ...props }) => (
    <input {...props} style={{ display: 'none' }} />
  )

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('mesComptes.import.step2.title') /* LAP-733 (GE022) */}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('mesComptes.import.step2.description') /* LAP-733 (GE022) */}
      </Typography>

      <Grid container spacing={2}>
        {/* CSV Upload */}
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center' }}>
            <label htmlFor="csv-upload">
              <Input
                accept=".csv,.xlsx,.xls"
                id="csv-upload"
                onChange={handleFileUpload}
                type="file"
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<FileUploadIcon />}
                fullWidth
                sx={{ py: 2 }}
              >
                {t('mesComptes.import.step2.uploadButton') /* LAP-733 (GE022) */}
              </Button>
            </label>
            {uploadedFile && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={uploadedFile.name}
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon />}
                />
                {fileData?.columns && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {t('mesComptes.import.step2.detectedColumns') /* LAP-733 (GE022) */}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {fileData.columns.join(', ')}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t('mesComptes.import.step2.totalRows', { count: fileData.rowCount }) /* LAP-733 (GE022) */}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Information Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          {t('mesComptes.import.step2.requirementsTitle') /* LAP-733 (GE022) */}
        </Typography>
        <Typography variant="body2" component="div">
          <Trans
            i18nKey="mesComptes.import.step2.requirementsBody"
            ns="layout"
            components={{ b: <strong />, br: <br /> }}
          />
          {/* LAP-733 (GE022) */}
        </Typography>
      </Alert>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>
          {t('mesComptes.import.back') /* LAP-733 (GE022) */}
        </Button>
        <Button variant="contained" onClick={handleValidateAndNext}>
          {t('mesComptes.import.step2.nextButton') /* LAP-733 (GE022) */}
        </Button>
      </Box>
    </Box>
  )
}

export default FileUploadStep
